import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { Plus, Trash2 } from "lucide-react";

const slugify = (s) => (s || "").toLowerCase().replace(/['"]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

const BLANK = { slug: "", title: "", excerpt: "", body_markdown: "", hero_image_url: "", author: "Taimur Ahmed", status: "draft", published_at: "" };

export default function AdminBlog() {
  const [list, setList] = useState({ items: [], total: 0 });
  const [filter, setFilter] = useState({ q: "", status: "" });
  const [selected, setSelected] = useState(null);  // post_id or "new"
  const [draft, setDraft] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const p = new URLSearchParams({ page_size: "100" });
    if (filter.status) p.set("status", filter.status);
    try { setList((await api.get(`/admin/blog/posts?${p.toString()}`)).data); } catch {}
  };
  useEffect(() => { reload(); }, [filter.status]);

  const items = list.items.filter((p) => !filter.q || (p.title || "").toLowerCase().includes(filter.q.toLowerCase()) || (p.slug || "").includes(filter.q.toLowerCase()));

  const startNew = () => { setSelected("new"); setDraft(BLANK); };
  const select = (p) => { setSelected(p.post_id || p.slug); setDraft({
    slug: p.slug || "", title: p.title || "", excerpt: p.excerpt || "",
    body_markdown: p.body_markdown || "", hero_image_url: p.hero_image_url || "",
    author: p.author || "Taimur Ahmed", status: p.status || "draft",
    published_at: (p.published_at || "").slice(0, 10),
  }); };

  const save = async () => {
    if (!draft.title) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      if (selected === "new") {
        const payload = { ...draft, slug: draft.slug || slugify(draft.title) };
        if (!payload.published_at) delete payload.published_at;
        const r = await api.post(`/admin/blog/posts`, payload);
        toast.success("Created"); await reload(); setSelected(r.data.post_id);
      } else {
        const payload = { ...draft };
        if (!payload.published_at) delete payload.published_at;
        await api.patch(`/admin/blog/posts/${selected}`, payload);
        toast.success("Saved"); await reload();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const publish = async () => {
    if (selected === "new" || !selected) { toast.error("Save first"); return; }
    try { await api.post(`/admin/blog/posts/${selected}/publish`); toast.success("Published"); reload(); setDraft({ ...draft, status: "published" }); }
    catch { toast.error("Publish failed"); }
  };
  const del = async () => {
    if (selected === "new" || !selected) return;
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/admin/blog/posts/${selected}`); toast.success("Deleted"); setSelected(null); setDraft(BLANK); reload(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-blog-page">
      <header className="flex items-baseline justify-between flex-wrap gap-3">
        <div><h1 className="font-serif text-3xl text-ink tracking-tight">Blog</h1><p className="text-ink-soft text-sm mt-1">{list.total} posts</p></div>
        <button onClick={startNew} className="inline-flex items-center gap-2 border border-ivy bg-ivy text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-2.5 px-5" data-testid="blog-new-btn">
          <Plus className="h-3.5 w-3.5" /> New post
        </button>
      </header>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* List */}
        <aside className="lg:col-span-4 border border-stone/40 bg-bone">
          <div className="p-3 border-b border-stone/30 space-y-2">
            <input value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} placeholder="Search title/slug…"
              className="w-full bg-bone-dim border border-stone/40 px-2 py-1.5 text-xs" data-testid="blog-search" />
            <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="w-full bg-bone-dim border border-stone/40 px-2 py-1.5 text-xs" data-testid="blog-status-filter">
              <option value="">All statuses</option>
              <option value="draft">draft</option>
              <option value="published">published</option>
            </select>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {items.map((p) => {
              const id = p.post_id || p.slug;
              const isSel = selected === id;
              return (
                <button key={id} onClick={() => select(p)}
                  className={`w-full text-left px-3 py-2.5 border-b border-stone/20 flex items-baseline justify-between gap-2 ${isSel ? "bg-bone-dim" : "hover:bg-bone-dim/50"}`}
                  data-testid={`blog-item-${p.slug}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-ink truncate">{p.title || "Untitled"}</div>
                    <div className="text-[10px] text-stone font-mono truncate">{p.slug}</div>
                  </div>
                  <span className={`text-[9px] uppercase tracking-[0.18em] border px-1.5 py-0.5 ${p.status === "published" ? "border-ivy text-ivy" : "border-brass text-brass"}`}>{p.status || (p.published_at ? "published" : "draft")}</span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Editor */}
        <section className="lg:col-span-8" data-testid="blog-editor">
          {!selected ? (
            <div className="border border-stone/40 bg-bone p-8 text-center text-ink-soft">
              <p className="text-sm">Select a post on the left or click <span className="text-ivy">New post</span> to start.</p>
            </div>
          ) : (
            <div className="border border-stone/40 bg-bone p-5 space-y-4">
              <div className="grid sm:grid-cols-3 gap-3">
                <label className="text-xs sm:col-span-2">Title<input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} onBlur={() => { if (selected === "new" && !draft.slug) setDraft((d) => ({ ...d, slug: slugify(d.title) })); }} className="mt-1 w-full bg-bone-dim border border-stone/40 px-3 py-2 text-base font-serif" data-testid="blog-title" /></label>
                <label className="text-xs">Slug<input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} className="mt-1 w-full bg-bone-dim border border-stone/40 px-3 py-2 text-sm font-mono" data-testid="blog-slug" /></label>
              </div>
              <label className="text-xs block">Excerpt<input value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })} className="mt-1 w-full bg-bone-dim border border-stone/40 px-3 py-2 text-sm" data-testid="blog-excerpt" /></label>
              <div className="grid sm:grid-cols-3 gap-3">
                <label className="text-xs">Hero image URL<input value={draft.hero_image_url} onChange={(e) => setDraft({ ...draft, hero_image_url: e.target.value })} className="mt-1 w-full bg-bone-dim border border-stone/40 px-3 py-2 text-sm font-mono" data-testid="blog-hero" /></label>
                <label className="text-xs">Author<input value={draft.author} onChange={(e) => setDraft({ ...draft, author: e.target.value })} className="mt-1 w-full bg-bone-dim border border-stone/40 px-3 py-2 text-sm" data-testid="blog-author" /></label>
                <label className="text-xs">Status<select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} className="mt-1 w-full bg-bone-dim border border-stone/40 px-3 py-2 text-sm" data-testid="blog-status">
                  <option value="draft">draft</option><option value="published">published</option>
                </select></label>
              </div>

              <div>
                <div className="text-xs mb-1">Body (Markdown · live preview on the right)</div>
                <div className="grid lg:grid-cols-2 gap-3">
                  <textarea value={draft.body_markdown} onChange={(e) => setDraft({ ...draft, body_markdown: e.target.value })} rows={18}
                    placeholder="# Heading&#10;&#10;Your post body in markdown…"
                    className="w-full bg-bone-dim border border-stone/40 px-3 py-2 text-xs font-mono leading-relaxed" data-testid="blog-body" />
                  <div className="border border-stone/40 bg-bone-dim px-4 py-3 prose prose-sm max-w-none overflow-auto h-[450px]" data-testid="blog-preview">
                    <ReactMarkdown>{draft.body_markdown || "*Preview will appear here as you type…*"}</ReactMarkdown>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 pt-2 border-t border-stone/30">
                <button onClick={save} disabled={busy} className="border border-ivy bg-ivy text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-2.5 px-5 disabled:opacity-60" data-testid="blog-save-btn">{busy ? "Saving…" : "Save"}</button>
                <button onClick={publish} disabled={selected === "new"} className="border border-brass text-brass hover:bg-brass hover:text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-2.5 px-5 disabled:opacity-40" data-testid="blog-publish-btn">Publish</button>
                {selected !== "new" && <button onClick={del} className="ml-auto inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-stone hover:text-brass" data-testid="blog-delete-btn"><Trash2 className="h-3 w-3" /> Delete</button>}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
